// src/pages/LoginPage.jsx
import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { C } from './formui';
import { APP_VERSION } from '../lib/version';
import { LOGO_DATA_URL } from '../lib/logo';

// Das Logo kommt aus der App selbst, nicht von etb-karlsruhe.de. Vorher wurde
// es über das Netz geladen - im Trafohäuschen ohne Empfang blieb an der
// Anmeldung ein leerer Kasten. Genau dafür liegt es in lib/logo.js eingebettet.
const LOGO = LOGO_DATA_URL;

export default function LoginPage() {
  const { signIn, resetPassword, error } = useAuth();
  const [mode, setMode] = useState('login'); // 'login' | 'reset' | 'reset-sent'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetMsg, setResetMsg] = useState('');
  const [showPw, setShowPw] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    await signIn(email, password);
    setLoading(false);
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await resetPassword(email);
    setLoading(false);
    if (result.success) {
      setMode('reset-sent');
    } else {
      setResetMsg(result.message);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', background: `linear-gradient(145deg, ${C.navy} 0%, ${C.navyMid} 60%, ${C.navyLight} 100%)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Segoe UI', system-ui, sans-serif", padding: 20,
    }}>
      {/* Background decoration */}
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        <div style={{ position: 'absolute', top: -100, right: -100, width: 400, height: 400, borderRadius: '50%', background: `radial-gradient(circle, ${C.yellow}15 0%, transparent 70%)` }}/>
        <div style={{ position: 'absolute', bottom: -80, left: -80, width: 300, height: 300, borderRadius: '50%', background: `radial-gradient(circle, ${C.yellow}10 0%, transparent 70%)` }}/>
      </div>

      <div style={{ width: '100%', maxWidth: 400, position: 'relative', zIndex: 1 }}>
        {/* Logo & Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <img src={LOGO} alt="ETB" style={{ height: 48, objectFit: 'contain', marginBottom: 16 }}
            onError={e => { e.target.style.display = 'none'; }}/>
          <div style={{ color: C.yellow, fontWeight: 900, fontSize: 22, letterSpacing: 0.3 }}>
            Wartungsprotokoll
          </div>
          <div style={{ color: C.gray300, fontSize: 15, marginTop: 4 }}>
            EnergieTechnik Becker GmbH
          </div>
        </div>

        {/* Card */}
        <div style={{
          background: C.white, borderRadius: 16, padding: 32,
          boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
          border: `1px solid rgba(255,255,255,0.1)`,
        }}>
          {/* --- LOGIN --- */}
          {mode === 'login' && (<>
            <div style={{ fontSize: 20, fontWeight: 800, color: C.navy, marginBottom: 6 }}>Anmelden</div>
            <div style={{ fontSize: 15, color: C.gray500, marginBottom: 24 }}>Bitte melde dich mit deinen Zugangsdaten an.</div>

            {error && (
              <div style={{ background: C.redBg, border: `1px solid ${C.red}`, borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 15, color: C.red }}>
                ⚠️ {error}
              </div>
            )}

            <form onSubmit={handleLogin}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 13, fontWeight: 700, color: C.gray500, textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', marginBottom: 5 }}>
                  E-Mail-Adresse
                </label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="name@etb-karlsruhe.de" required autoComplete="email"
                  style={{
                    width: '100%', padding: '12px 14px', borderRadius: 9, fontSize: 15,
                    border: `1.5px solid ${C.gray200}`, outline: 'none', boxSizing: 'border-box',
                    color: C.navy, background: C.white,
                  }}
                  onFocus={e => e.target.style.borderColor = C.yellow}
                  onBlur={e => e.target.style.borderColor = C.gray200}
                />
              </div>

              <div style={{ marginBottom: 8 }}>
                <label style={{ fontSize: 13, fontWeight: 700, color: C.gray500, textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', marginBottom: 5 }}>
                  Passwort
                </label>
                <div style={{ position: 'relative' }}>
                  <input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••" required autoComplete="current-password"
                    style={{
                      width: '100%', padding: '12px 44px 12px 14px', borderRadius: 9, fontSize: 15,
                      border: `1.5px solid ${C.gray200}`, outline: 'none', boxSizing: 'border-box',
                      color: C.navy, background: C.white,
                    }}
                    onFocus={e => e.target.style.borderColor = C.yellow}
                    onBlur={e => e.target.style.borderColor = C.gray200}
                  />
                  <button type="button" onClick={() => setShowPw(!showPw)}
                    style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: C.gray300 }}>
                    {showPw ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>

              <div style={{ textAlign: 'center', marginBottom: 20, padding: '8px 12px', background: C.gray100, borderRadius: 7 }}>
                <span style={{ fontSize: 13, color: C.gray500 }}>
                  Passwort vergessen? Wende dich an deinen Admin.
                </span>
              </div>

              <button type="submit" disabled={loading || !email || !password}
                style={{
                  width: '100%', padding: '14px', borderRadius: 10, fontSize: 16, fontWeight: 800,
                  background: loading ? C.gray200 : C.yellow, color: loading ? C.gray500 : C.navy,
                  border: 'none', cursor: loading ? 'not-allowed' : 'pointer', transition: 'all 0.15s',
                }}>
                {loading ? '⏳ Anmelden...' : '→ Anmelden'}
              </button>
            </form>
          </>)}

          {/* --- PASSWORT VERGESSEN --- */}
          {mode === 'reset' && (<>
            <div style={{ fontSize: 20, fontWeight: 800, color: C.navy, marginBottom: 6 }}>Passwort zurücksetzen</div>
            <div style={{ fontSize: 15, color: C.gray500, marginBottom: 24 }}>
              Gib deine E-Mail-Adresse ein. Du erhältst einen Link zum Zurücksetzen.
            </div>

            {resetMsg && (
              <div style={{ background: C.redBg, border: `1px solid ${C.red}`, borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 15, color: C.red }}>
                ⚠️ {resetMsg}
              </div>
            )}

            <form onSubmit={handleReset}>
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 13, fontWeight: 700, color: C.gray500, textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', marginBottom: 5 }}>
                  E-Mail-Adresse
                </label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="name@etb-karlsruhe.de" required
                  style={{
                    width: '100%', padding: '12px 14px', borderRadius: 9, fontSize: 15,
                    border: `1.5px solid ${C.gray200}`, outline: 'none', boxSizing: 'border-box',
                  }}
                  onFocus={e => e.target.style.borderColor = C.yellow}
                  onBlur={e => e.target.style.borderColor = C.gray200}
                />
              </div>

              <button type="submit" disabled={loading || !email}
                style={{
                  width: '100%', padding: '14px', borderRadius: 10, fontSize: 15, fontWeight: 800,
                  background: C.yellow, color: C.navy, border: 'none', cursor: 'pointer', marginBottom: 12,
                }}>
                {loading ? '⏳ Sende...' : '📧 Reset-Link senden'}
              </button>

              <button type="button" onClick={() => setMode('login')}
                style={{ width: '100%', padding: '11px', borderRadius: 10, fontSize: 14, fontWeight: 600, background: 'none', border: `1.5px solid ${C.gray200}`, cursor: 'pointer', color: C.gray500 }}>
                ← Zurück zum Login
              </button>
            </form>
          </>)}

          {/* --- RESET BESTÄTIGUNG --- */}
          {mode === 'reset-sent' && (<>
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>📧</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: C.navy, marginBottom: 8 }}>E-Mail gesendet!</div>
              <div style={{ fontSize: 15, color: C.gray500, marginBottom: 24, lineHeight: 1.6 }}>
                Wir haben einen Link zum Zurücksetzen deines Passworts an <strong>{email}</strong> gesendet.
                Überprüfe auch deinen Spam-Ordner.
              </div>
              <button onClick={() => setMode('login')}
                style={{
                  width: '100%', padding: '14px', borderRadius: 10, fontSize: 15, fontWeight: 800,
                  background: C.yellow, color: C.navy, border: 'none', cursor: 'pointer',
                }}>
                → Zurück zum Login
              </button>
            </div>
          </>)}
        </div>

        {/* Footer */}
        <div style={{ textAlign: 'center', marginTop: 20, color: `${C.gray300}80`, fontSize: 13 }}>
          EnergieTechnik Becker GmbH · Karlsruhe · {APP_VERSION}
        </div>
      </div>
    </div>
  );
}
